import { useEffect, useState } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { mediaKind } from '@/lib/utils'

interface Props {
  /** Media URLs to view, or null when closed. */
  urls: string[] | null
  onClose: () => void
}

/** Fullscreen media viewer — images & videos, sized to the screen, with
 *  keyboard + arrow navigation when there are several files. */
export function MediaLightbox({ urls, onClose }: Props) {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    setIndex(0)
  }, [urls])

  useEffect(() => {
    if (!urls) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, (urls?.length ?? 1) - 1))
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [urls, onClose])

  if (!urls || urls.length === 0) return null

  const url = urls[Math.min(index, urls.length - 1)]
  const kind = mediaKind(url)
  const hasNav = urls.length > 1

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/90 p-6"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-5 top-5 rounded-full bg-white/90 p-2.5 text-slate-700 shadow-lg transition-colors hover:bg-white"
        aria-label="Fermer"
      >
        <X className="h-5 w-5" />
      </button>

      {hasNav && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setIndex((i) => Math.max(i - 1, 0))
            }}
            disabled={index === 0}
            className="absolute left-5 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2.5 text-slate-700 shadow-lg transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="Précédent"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setIndex((i) => Math.min(i + 1, urls.length - 1))
            }}
            disabled={index === urls.length - 1}
            className="absolute right-5 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2.5 text-slate-700 shadow-lg transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="Suivant"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      <div
        className="flex max-h-full max-w-full flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        {kind === 'video' ? (
          <video
            src={url}
            controls
            autoPlay
            className="max-h-[85vh] max-w-[90vw] rounded-2xl shadow-2xl"
          />
        ) : kind === 'audio' ? (
          <div className="rounded-2xl bg-white px-6 py-8 shadow-2xl">
            <audio src={url} controls autoPlay className="w-[60vw] max-w-md" />
          </div>
        ) : kind === 'image' ? (
          <img
            src={url}
            alt=""
            className="max-h-[85vh] max-w-[90vw] rounded-2xl object-contain shadow-2xl"
          />
        ) : kind === 'pdf' ? (
          <iframe
            src={url}
            title="PDF"
            className="h-[85vh] w-[90vw] max-w-5xl rounded-2xl border-0 bg-white shadow-2xl"
          />
        ) : (
          <a
            href={url}
            target="_blank"
            rel="noopener"
            className="rounded-2xl bg-white px-6 py-4 text-sm font-medium text-[var(--color-link)] shadow-2xl hover:underline"
          >
            Ouvrir le fichier ↗
          </a>
        )}

        {hasNav && (
          <div className="rounded-full bg-white/90 px-3 py-1 text-[12px] font-medium tabular-nums text-slate-700">
            {index + 1} / {urls.length}
          </div>
        )}
      </div>
    </div>
  )
}
